import type { Fraction } from "../shapes/common.js";

type AcceptableValue = number | bigint | BigFraction | Fraction;

/**
 * Nominal typing of unsigned BigInt.
 *
 * @see https://basarat.gitbooks.io/typescript/docs/tips/nominalTyping.html
 */
type UBigInt = bigint & {
  _UBigIntBrand: string; // To prevent type errors
};

const BIG_INT_1 = 1n as UBigInt;
const BIG_INT_10 = 10n as UBigInt;

// Using this we make sure returned values are absolute (never negatives)
const uMultiply = (a: UBigInt, b: UBigInt) => (a * b) as UBigInt;
const uRemainder = (a: UBigInt, b: UBigInt) => (a % b) as UBigInt;

const uExponentiate = (a: UBigInt, b: UBigInt) => (a ** b) as UBigInt;
// Extra UBigInt based functions
const isNeg = (value: bigint) => value < 0;

const abs = (value: bigint) => (isNeg(value) ? -value : value) as UBigInt;

/**
 * Transforms an unsigned BigInt into a potentially signed one depending on
 * `shouldNegate`.
 */
const sign = (value: UBigInt, shouldNegate: boolean) =>
  shouldNegate ? -(value as bigint) : value;

const checkPlace = (place: number) => {
  if (place < 0) {
    throw new Error("Place argument should be a positive number");
  }
  return place;
};

/**
 * Finds the common denominator for the given unsigned BigInt.
 */
const gcd = (left: UBigInt, right: UBigInt) => {
  while (right !== 0n) {
    const remain = uRemainder(left, right);
    left = right;
    right = remain;
  }

  return left;
};

interface Parts {
  readonly neg: boolean;
  readonly n: UBigInt;
  readonly d: UBigInt;
}

export class BigFraction {
  private readonly parts: Parts;

  constructor(parts: Parts) {
    if (parts.d === 0n) {
      throw new RangeError("The denominator of a fraction can not be zero");
    }

    // simplify
    const commonDenominator = gcd(parts.d, parts.n);
    this.parts = {
      neg: parts.neg,
      n: abs(parts.n / commonDenominator),
      d: abs(parts.d / commonDenominator),
    };
  }

  equals(value: AcceptableValue) {
    if (this === value) {
      return true;
    }

    const fraction = makeFraction(value);

    return (
      (this.parts.neg ? -1n : 1n) * this.parts.n * fraction.parts.d ===
      (fraction.parts.neg ? -1n : 1n) * this.parts.d * fraction.parts.n
    );
  }

  mul(value: AcceptableValue) {
    const fraction = makeFraction(value);

    return new BigFraction({
      neg: this.parts.neg !== fraction.parts.neg,
      n: uMultiply(this.parts.n, fraction.parts.n),
      d: uMultiply(this.parts.d, fraction.parts.d),
    });
  }

  div(value: AcceptableValue) {
    const fraction = makeFraction(value);
    const d = uMultiply(this.parts.d, fraction.parts.n);

    if (d === 0n) {
      throw new RangeError("Can not divide a fraction by zero");
    }

    return new BigFraction({
      neg: this.parts.neg !== fraction.parts.neg,
      n: uMultiply(this.parts.n, fraction.parts.d),
      d,
    });
  }

  add(value: AcceptableValue) {
    const fraction = makeFraction(value);

    const left = sign(
      uMultiply(this.parts.n, fraction.parts.d),
      this.parts.neg,
    );
    const right = sign(
      uMultiply(this.parts.d, fraction.parts.n),
      fraction.parts.neg,
    );

    const sum = left + right;
    return new BigFraction({
      neg: isNeg(sum),
      n: abs(sum),
      d: uMultiply(this.parts.d, fraction.parts.d),
    });
  }

  sub(value: AcceptableValue) {
    const fraction = makeFraction(value);

    const left = sign(
      uMultiply(this.parts.n, fraction.parts.d),
      this.parts.neg,
    );
    const right = sign(
      uMultiply(this.parts.d, fraction.parts.n),
      fraction.parts.neg,
    );

    const subtraction = left - right;
    return new BigFraction({
      neg: isNeg(subtraction),
      n: abs(subtraction),
      d: uMultiply(this.parts.d, fraction.parts.d),
    });
  }

  compare(value: AcceptableValue) {
    return (
      this.sub(value).isNeg() ? -1
      : this.equals(value) ? 0
      : 1
    );
  }

  neg() {
    return new BigFraction({
      neg: !this.parts.neg,
      n: this.parts.n,
      d: this.parts.d,
    });
  }

  abs() {
    return new BigFraction({ neg: false, n: this.parts.n, d: this.parts.d });
  }

  floor(place: number) {
    const shift = uExponentiate(BIG_INT_10, abs(BigInt(checkPlace(place))));
    return new BigFraction({
      neg: this.parts.neg,
      n: abs(BigInt(Math.floor(this.mul(shift).valueOf()))),
      d: shift,
    });
  }

  ceil(place: number) {
    const shift = uExponentiate(BIG_INT_10, abs(BigInt(checkPlace(place))));
    return new BigFraction({
      neg: this.parts.neg,
      n: abs(BigInt(Math.ceil(this.mul(shift).valueOf()))),
      d: shift,
    });
  }

  /**
   * Round half towards positive infinity (similar to Math.round)
   */
  round(place: number) {
    const shift = uExponentiate(BIG_INT_10, abs(BigInt(checkPlace(place))));
    return new BigFraction({
      neg: this.parts.neg,
      n: abs(BigInt(Math.round(this.mul(shift).valueOf()))),
      d: shift,
    });
  }

  isNeg() {
    return this.parts.neg;
  }

  toFraction() {
    return `${this.parts.neg ? "-" : ""}${this.parts.n}/${
      this.parts.d
    }` as Fraction;
  }

  toString() {
    return this.toFraction();
  }

  /**
   * Transforms the fraction in a number.
   *
   * This should be only use for display purposes because we take the risk of
   * loosing precision.
   *
   * @return the decimal representation of the fraction
   */
  valueOf(): number {
    let nominator = this.parts.n as bigint;
    let denominator = this.parts.d as bigint;

    // Attempt to reduce precision if any of the nominators or denominator is
    // out of the range to be converted to a number.
    while (
      (Number.MAX_VALUE < nominator || Number.MAX_VALUE < denominator) &&
      10n <= nominator &&
      10n <= denominator
    ) {
      nominator /= 10n;
      denominator /= 10n;
    }

    return Number(sign(abs(nominator), this.parts.neg)) / Number(denominator);
  }
}

export const bigFractionFromBigInt = (value: bigint) =>
  new BigFraction({
    neg: isNeg(value),
    n: abs(value),
    d: BIG_INT_1,
  });

/**
 * Extracts the parts of a decimal number to split the significand part from
 * the order of magnitude. It is helpful to create a fraction where the
 * denominator is a power of 10: 0.1 => 1/10.
 */
const extractNumberElements = (value: number) => {
  const [left, right] = value.toExponential().split("e");

  if (left == null || right == null) {
    throw new TypeError(
      `${value} is not a number we can transform to a fraction.`,
    );
  }

  const place = left.length - 1 - Math.max(0, left.indexOf("."));

  return {
    significand: BigInt(left.replace(".", "")),
    orderOfMagnitude: BigInt(Number.parseInt(right) - place),
  };
};

/**
 * Creates a fraction from a JS native number type.
 *
 * This is an unsafe way of creating a fraction since the number can be
 * imprecise. Prefer a fraction or BigInt based instantiation instead.
 */
export const bigFractionFromNumber = (value: number) => {
  if (Number.isSafeInteger(value)) {
    // short path, it is a safe number to convert
    return bigFractionFromBigInt(BigInt(value));
  } else {
    const { significand, orderOfMagnitude } = extractNumberElements(value);

    const bigFraction =
      isNeg(orderOfMagnitude) ?
        new BigFraction({
          neg: isNeg(significand),
          n: abs(significand),
          d: abs(uExponentiate(BIG_INT_10, abs(orderOfMagnitude))),
        })
      : new BigFraction({
          neg: isNeg(significand),
          n: uMultiply(
            abs(significand),
            uExponentiate(BIG_INT_10, abs(orderOfMagnitude)),
          ),
          d: BIG_INT_1,
        });

    return bigFraction;
  }
};

export const bigFractionFromFraction = (value: Fraction) => {
  const [left, right = 1] = value.split("/");

  if (left == null) {
    throw new TypeError(`${value} is not a respecting the fraction format.`);
  }

  const n = BigInt(left);
  const d = BigInt(right);

  return new BigFraction({
    neg: isNeg(n) !== isNeg(d),
    n: abs(n),
    d: abs(d),
  });
};

const makeFraction = (value: AcceptableValue) => {
  if (value instanceof BigFraction) {
    return value;
  } else if (typeof value === "bigint") {
    return bigFractionFromBigInt(value);
  } else if (typeof value === "number") {
    return bigFractionFromNumber(value);
  } else {
    return bigFractionFromFraction(value);
  }
};
